import React from "react";
import Detail from "./steps/details";
import Description from "./steps/description";
import Skills from "./steps/skills";
import Review from "./steps/review";
import ChevronLeftIcon from "@assets/svg/ChevronLeftIcon";
import { colors } from "@utils/theme";
import LoadingAnimation from "@assets/svg/LoadingAnimation";
import { isNullOrEmpty } from "@utils/helperFunction";
import CrossBig from "@assets/svg/CrossBig";
import { Check, Search } from "lucide-react";
import dayjs from "dayjs";
// @ts-ignore
import { ReactComponent as Success } from "@assets/svg/success.svg";
import AdditionalNotes from "./steps/AdditionalNotes";
import AddMembers from "./steps/AddMembers";
import CustomInput from "@components/CustomInput/CustomInput";
import { StepConfig } from "./create.types";
import useCreateOrEditJobRequirements from "./hooks/create.controller";
import { PartnerCompany } from "../recruitmentAgents/partnerAgencies/partnerAgencies.types";

export const STEP_CONFIG: StepConfig[] = [
  { label: "Details", component: Detail },
  { label: "Description", component: Description },
  { label: "Skills", component: Skills },
  { label: "Additional notes", component: AdditionalNotes },
  { label: "Add members", component: AddMembers },
  { label: "Review", component: Review },
];

const BackButton = ({ navigate, navigateTo, label }) => (
  <button
    onClick={() => (navigateTo ? navigate(navigateTo) : navigate())}
    className="flex items-center cursor-pointer"
  >
    <ChevronLeftIcon className="w-4 h-4" fill="#4d4d4d" />
    <label className="text-xs font-medium text-primary">{label}</label>
  </button>
);

const AddOrEditJobRequirement: React.FC = () => {
  const {
    step,
    control,
    errors,
    fields,
    setValue,
    watch,
    setStep,
    navigate,
    trigger,
    filteredPartnerAgencuesData,
    defaultValues,
    sendJob,
    StepComponent,
    handleNext,
  } = useCreateOrEditJobRequirements();

  const formatNumber = (num: string | number) => {
    return Number(num) > 9 ? num : num.toString().padStart(2, "0");
  };

  return (
    <div className="flex flex-col rounded-lg bg-white w-full flex-grow p-6">
      <BackButton navigate={navigate} navigateTo={-1} label="Back to listing" />

      <div className="flex justify-center items-center my-6 py-6 w-full bg-[#F1F5F9] rounded">
        {STEP_CONFIG.map((s, index) => (
          <div key={index} className="flex items-center gap-4">
            {index !== 0 && (
              <div className="w-10 h-px border border-b border-light-black ml-4" />
            )}
            <div
              // onClick={() => setActiveStep(stepIndex + 1)}
              className={`flex gap-6 ${
                step === index ? "bg-primary" : "bg-white"
              } rounded items-center`}
            >
              <div className="flex items-center gap-2">
                <label
                  className={`font-medium text-xs px-4 py-2 ${
                    step === index ? "text-white" : "text-primary"
                  }`}
                >
                  {s.label}
                </label>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="flex flex-col py-5 flex-1 overflow-y-scroll no-scrollbar w-full">
        <StepComponent
          control={control}
          errors={errors}
          setValue={setValue}
          watch={watch}
          trigger={trigger}
          createdDraftJob={fields?.createdDraftJob}
        />
      </div>

      <div
        className={`flex   mt-6 ${
          step === 3 ? "justify-end" : "justify-between"
        }`}
      >
        {step !== 3 && (
          <button
            type="button"
            onClick={() => setStep((prev) => Math.max(prev - 1, 0))}
            disabled={step === 0}
            className={`bg-[#F7F7F7] rounded-md text-xs px-4 py-2 self-end text-primary
       ${step === 1 ? "opacity-50 cursor-not-allowed" : ""}`}
          >
            Back
          </button>
        )}
        <div className="flex gap-5">
          {step !== 1 && step !== 3 && (
            <button
              type="button"
              disabled={fields?.isLoading}
              className="rounded-md text-xs px-4 py-2 self-end"
            >
              Save draft
            </button>
          )}
          <button
            type="button"
            onClick={handleNext}
            disabled={fields?.isLoading}
            className="bg-primary flex items-center text-white rounded-md text-xs px-4 py-2 self-end"
          >
            {fields?.isLoading && (
              <LoadingAnimation color={colors.White} className="w-4 h-4" />
            )}
            {step === STEP_CONFIG.length - 1 ? "Submit" : "Continue"}
          </button>
        </div>
      </div>

      {fields?.inviteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded-lg w-2/3 h-2/3 overflow-hidden">
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-sm font-bold">
                Select Agents to submit this job requirement to
              </h2>
              <button
                onClick={() => {
                  setValue("inviteModal", false);
                  navigate("/requirements", { replace: true });
                }}
              >
                <CrossBig width={24} height={24} />
              </button>
            </div>

            {isNullOrEmpty(fields?.partnerAgencues) ? (
              <div className="flex-col items-center justify-center flex-1 text-center">
                <p className="text-xs text-[#6F6F6F] font-medium">
                  No partner agencies found
                </p>
              </div>
            ) : (
              <div className="flex flex-1 flex-col h-[100%]">
                <div className="flex  items-center justify-between w-full gap-8 bg-[#F1F5F9] rounded-lg px-3 py-3 mb-5">
                  <div className="relative w-1/2">
                    {/* @ts-ignore */}
                    <CustomInput
                      name="jobSearch"
                      control={control}
                      className="border-none"
                      errors={errors}
                      placeholder="Search by job title"
                      renderRight={<Search color="#6F6F6F" width={16} />}
                    />
                  </div>
                  <button
                    className={`bg-[#4D4D4D] items-center flex text-white px-3 py-[10px] font-semibold rounded-md text-[10px] ${
                      fields?.selectedJobs?.length === 0
                        ? "opacity-50"
                        : "opacity-100"
                    }`}
                    disabled={
                      fields?.selectedJobs?.length === 0 || fields.isSubmitting
                    }
                    onClick={sendJob}
                  >
                    {fields.isSubmitting && (
                      <LoadingAnimation color={colors.White} />
                    )}
                    Send job requirement
                  </button>
                </div>
                <div className="flex h-[100%] overflow-y-scroll no-scrollbar">
                  {isNullOrEmpty(filteredPartnerAgencuesData) ? (
                    <div className="flex-col items-center justify-center flex-1 text-center">
                      <p className="text-xs text-[#6F6F6F] font-medium">
                        No search result found!
                      </p>
                    </div>
                  ) : (
                    <div className="w-[100%]">
                      <table className="min-w-full text-xs text-left table-fixed">
                        <thead className="border-b border-[#E0E0E0]">
                          <tr className="font-semibold">
                            <th className="text-xs font-bold text-[#6F6F6F]"></th>
                            <th className="py-3 text-xs font-bold text-[#6F6F6F]">
                              Recruitment Agent
                            </th>
                            <th className="py-3 text-xs font-bold text-[#6F6F6F]">
                              Company
                            </th>
                            <th className="py-3 text-xs font-bold text-[#6F6F6F] text-center">
                              Open Jobs
                            </th>
                            <th className="py-3 text-xs font-bold text-[#6F6F6F] ">
                              Working since
                            </th>
                            <th className="py-3 text-xs font-bold text-[#6F6F6F]">
                              Location
                            </th>
                          </tr>
                        </thead>
                        <tbody className="text-gray-700">
                          {filteredPartnerAgencuesData?.map(
                            (agent: PartnerCompany, idx) => (
                              <tr
                                key={idx}
                                className="border-t  border-gray-100 cursor-pointer hover:bg-gray-50"
                              >
                                <td>
                                  <div
                                    className={`flex ml-2  border border-primary items-center justify-center rounded h-[18px] w-[18px] hover:bg-gray-200 ${
                                      fields.selectedAgents?.includes(
                                        agent?.recruiterId
                                      ) && "bg-primary hover:bg-light-black"
                                    }`}
                                    role="button"
                                    onClick={() => {
                                      if (
                                        fields.selectedAgents?.includes(
                                          agent?.recruiterId
                                        )
                                      ) {
                                        setValue(
                                          "selectedAgents",
                                          fields?.selectedAgents?.filter(
                                            (x) => x !== agent?.recruiterId
                                          )
                                        );
                                      } else {
                                        setValue("selectedAgents", [
                                          ...fields.selectedAgents,
                                          agent?.recruiterId,
                                        ]);
                                      }
                                    }}
                                  >
                                    {fields.selectedAgents?.includes(
                                      agent?.recruiterId
                                    ) && (
                                      <Check
                                        width={20}
                                        height={20}
                                        color={colors.White}
                                      />
                                    )}
                                  </div>
                                </td>
                                <td className="text-xs font-medium text-[#6F6F6F] ">
                                  <div className="flex gap-2 items-center">
                                    <div
                                      className={`h-8 w-8 rounded-full justify-center items-center flex bg-nwhited ${
                                        !agent?.recruiter?.profilePic &&
                                        "bg-[#E1E0E0]"
                                      }`}
                                    >
                                      {agent?.recruiter?.profilePic ? (
                                        <img
                                          src={agent?.recruiter?.profilePic}
                                          className="h-full w-full object-cover rounded-full"
                                          alt="hiringCompany_profilePic"
                                        />
                                      ) : (
                                        <span className="font-montserrat text-white font-semibold text-sm uppercase">
                                          {agent?.recruiter?.firstName?.charAt(
                                            0
                                          )}
                                        </span>
                                      )}
                                    </div>
                                    {agent?.recruiter?.firstName}{" "}
                                    {agent?.recruiter?.lastName}
                                  </div>
                                </td>
                                <td className="py-6 text-xs font-medium text-[#6F6F6F]">
                                  {agent?.agentCompany?.name}
                                </td>
                                <td className="py-6 text-xs text-[#6F6F6F] text-center">
                                  {formatNumber(agent?.totalOpenJobs)}
                                </td>
                                <td className="py-6 text-xs font-medium text-[#6F6F6F]">
                                  {dayjs(agent?.recruiter?.createdAt).format(
                                    "MMMM DD, YYYY"
                                  )}
                                </td>
                                <td className="py-6 text-xs text-[#6F6F6F]">
                                  {isNullOrEmpty(agent.recruiter?.location)
                                    ? "-"
                                    : agent.recruiter?.location}
                                </td>
                              </tr>
                            )
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {fields?.openSucessModel && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="flex  flex-col bg-white p-6 rounded-lg  w-2/3 h-2/3 overflow-hidden">
            <div className="flex justify-end items-center mb-8">
              <button
                onClick={() => {
                  setValue("openSucessModel", !fields.openSucessModel);
                  navigate("/requirements", { replace: true });
                }}
              >
                <CrossBig width={24} height={24} />
              </button>
            </div>
            <div className="flex justify-center mt-14">
              <Success />
            </div>
            <div className="font-semibold text-xl text-primary text-center mt-8">
              Successfully sent!
            </div>
            <div className="font-medium text-xs text-light-black text-center mt-2">
              The job requirement has been successfully sent to the following
              recruiter agencies:
            </div>
            <button
              className={`bg-[#4D4D4D] flex mt-11 self-center items-center text-white px-3 py-[10px] font-semibold rounded-md text-[10px]`}
              onClick={() => {
                setValue("openSucessModel", !fields.openSucessModel);
                navigate("/requirements", { replace: true });
              }}
            >
              Back to partner agencies list
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddOrEditJobRequirement;
